import Image from "next/image";
import Award from "../icons/Award";
import { FC, HTMLAttributes, ReactNode } from "react";
import { IImage } from "@/api/dtos/shared-component.type";
import { getImageUrl } from "@/utils/media.helper";
import HtmlText from "@/sections/common/HtmlText";
import clsx from "clsx";
import Link from "next/link";

export type IAwardCard = {
  heading?: string;
  year?: string;
  description?: ReactNode;
  image?: IImage;
};

type IAwardCardProps = HTMLAttributes<HTMLDivElement> & {
  award: IAwardCard;
};

// ───────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────

const AwardImage: FC<{ image: IImage; alt: string }> = ({ image, alt }) => {
  const imageUrl = getImageUrl(image);

  const visual = imageUrl ? (
    <Image
      src={imageUrl}
      alt={alt}
      className="h-[250px] w-full object-contain transition-transform duration-300 group-hover:scale-105"
      width={400}
      height={250}
    />
  ) : (
    <Award
      className="h-[250px] w-full text-yellow-200 transition-transform duration-300 group-hover:scale-105"
      aria-hidden="true"
    />
  );

  if (image.href) {
    return (
      <Link
        href={image.href}
        target={image.target}
        className="block"
        aria-label={alt}
      >
        {visual}
      </Link>
    );
  }

  return visual;
};

// ───────────────────────────────────────────────────────────────
// ───────────────────────────────────────────────────────────────

const AwardCard: FC<IAwardCardProps> = (props) => {
  const { award, className, ...other } = props;

  const { year, heading, description, image } = award;
  const imageAlt = image?.name || year || "Award image";

  return (
    <div
      className={clsx(
        "group overflow-hidden border border-primary-100 shadow-sm",
        className,
      )}
      {...other}
    >
      {image && (
        <div className="px-5 py-6">
          <AwardImage image={image} alt={imageAlt} />
        </div>
      )}

      {(year || heading || description) && (
        <div className="flex-1 space-y-3 p-6">
          {year && (
            <div className="flex items-center gap-3">
              <p className="font-medium uppercase tracking-widest text-neutral-500 dark:text-neutral-400">
                {year}
              </p>
              <div className="h-px w-full bg-yellow-400" />
            </div>
          )}

          {heading && (
            <h3 className="text-h7 font-semibold text-primary-900">{heading}</h3>
          )}

          {description && <HtmlText>{description}</HtmlText>}
        </div>
      )}
    </div>
  );
};

export default AwardCard;
